import type { EnemyType, PvpRule, StackKind, ZoneId } from '../types';
import { SECRET_ENTRY, SPECIAL_FIND_XP } from './secrets';

export interface ZoneDef {
  id: ZoneId;
  /** Package 4, part 0 (owner, 7. 10. 2026): the world book's name (a proper name: "in Mercy General", no "the"). */
  name: string;
  /** What the zone was called before (Forest, Hospital …): the panel says it under the name ("Calder Woods · Forest"). */
  kind: string;
  /** The map's label where room is tight (Route 9, Steelworks, Bunker); missing: the name. */
  short?: string;
  danger: 1 | 2 | 3 | 4 | 5;
  recommended: [number, number];
  /** Level used to scale enemies and item level of loot. */
  level: number;
  /** Duration options in minutes; the first one is the standard run. */
  durations: number[];
  /** 3. 10. 2026 (package 2): a duration that opens later than the zone, by its minimum level (`zoneDurations`). */
  durationLevels?: Partial<Record<number, number>>;
  energy: number;
  pvp: PvpRule;
  phase: 1 | 2 | 3 | 4;
  /** Minimum survivor level to enter (locked on the map below this). */
  unlockLevel: number;
  secret?: boolean;
  /** Short description of the main loot, shown on the map. */
  lootText: string;
  resources: [StackKind, number][];
  /** Consumables and specials found here, with weights relative to resources. */
  extras: [StackKind, number][];
  enemies: [EnemyType, number][];
  /** Chance per tick for a gear item. Rarity and item pools: items/dropRates.ts. */
  itemChance: number;
  /** Map pin position in percent of the map canvas. */
  pos: { x: number; y: number };
  icon: string; // 24x24 path used on the map pin
  /** Package 4, part H: the zone in the world book (its landmark and who holds it), on the zone panel. */
  about: string;
  flavor: string[];
  /** Special drops per tick: [kind, chance]. */
  specials: [StackKind, number][];
  controllable: boolean;
  /** NPC Attack multiplier in this zone (krug 5, F6: tougher fights in danger 1 and 2). Default 1. */
  npcAttack?: number;
}

const PIN = {
  forest: 'M12 3 L5 13 H9 L4 20 H20 L15 13 H19 Z M12 20 V22',
  gas: 'M4 4 H14 V21 H4 Z M4 10 H14 M14 8 L18 10 V17 A2 2 0 0 0 21 17 V8 L18 5',
  downtown: 'M3 21 V9 H9 V21 M9 21 V4 H16 V21 M16 21 V12 H21 V21 M2 21 H22 M12 8 H13 M12 12 H13 M12 16 H13',
  hospital: 'M4 21 V6 H20 V21 M2 21 H22 M12 9 V15 M9 12 H15',
  factory: 'M3 21 V11 L8 14 V11 L13 14 V6 H17 V14 H21 V21 Z M2 21 H22',
  military: 'M12 3 L20 7 V13 C20 17 16 20 12 21 C8 20 4 17 4 13 V7 Z M9 12 L11 14 L15 10',
  mountains: 'M2 20 L9 8 L13 14 L16 10 L22 20 Z',
  wasteland: 'M3 18 C7 14 10 18 14 15 C17 13 19 15 21 14 M5 21 H19 M12 4 V7 M7 6 L8.5 8.5 M17 6 L15.5 8.5',
  bunker: 'M3 20 H21 M5 20 V13 A7 7 0 0 1 19 13 V20 M9 20 V16 H15 V20',
  lab: 'M9 3 H15 M10 3 V9 L4 19 A1.5 1.5 0 0 0 5.3 21 H18.7 A1.5 1.5 0 0 0 20 19 L14 9 V3 M7 15 H17',
  vault: 'M4 4 H20 V20 H4 Z M12 8 A4 4 0 1 1 11.99 8 Z M12 12 L14 10 M4 9 H2 M4 15 H2',
};

/** Krug 5 (K4.11): public zones that open when a survivor goes from level `from` to `to`. */
export function zonesOpenedBetween(from: number, to: number): ZoneId[] {
  return (Object.values(ZONES) as ZoneDef[]).filter((z) => !z.secret && z.unlockLevel > from && z.unlockLevel <= to).map((z) => z.id as ZoneId);
}

export const ZONES: Record<ZoneId, ZoneDef> = {
  forest: {
    id: 'forest', name: 'Calder Woods', kind: 'Forest', danger: 1, recommended: [1, 5], level: 3, durations: [10, 20, 40], energy: 1, pvp: 'similar', phase: 1, unlockLevel: 1,
    // Krug 5 (F6): NPCs hit harder in danger 1 and 2, so a fight costs 10–20% HP.
    npcAttack: 1.5,
    lootText: 'Food, Cloth, Wood, some Ammo',
    // Hunting shells (krug 5, K1; weight 8 since F6): about 1 Ammo per 20-minute run.
    resources: [['food', 40], ['wood', 35], ['cloth', 25], ['ammo', 8]],
    extras: [['bandage', 6], ['ration', 4], ['medicine', 3]],
    enemies: [['infected', 100]],
    itemChance: 0.07,
    pos: { x: 20, y: 64 }, icon: PIN.forest,
    about: 'The first people to run from the city came here. Their trailers are still parked, doors open.',
    flavor: ['Pine needles, wet ash, a road sign bent flat.', 'A deer watched from the ridge. Neither moved.', 'Found a tent. Nobody home for a long time.', 'Birds went quiet. Waited. They came back.', 'A hunter’s stand. The shells were still in the box.'],
    specials: [['old_map', 0.004]], controllable: false,
  },
  gas_station: {
    id: 'gas_station', name: 'Route 9 Gas & Go', kind: 'Gas Station', short: 'Route 9', danger: 2, recommended: [3, 8], level: 5, durations: [10, 20, 40], energy: 1, pvp: 'similar', phase: 1, unlockLevel: 1,
    npcAttack: 1.3,
    lootText: 'Fuel, Metal, Food',
    // Krug 5 (F5): Wood for Storage 3, a little more Ammo.
    resources: [['fuel', 20], ['metal', 35], ['food', 30], ['scrap', 10], ['wood', 20]],
    extras: [['ration', 4], ['bandage', 3], ['ammo', 6]],
    enemies: [['infected', 65], ['raider', 35]],
    itemChance: 0.07,
    pos: { x: 76, y: 62 }, icon: PIN.gas,
    about: 'The sign still says $3.19, the price on the Last Day. The Tollmen hold the pumps and charge for the road.',
    flavor: ['The pumps are dry. The smell isn’t.', 'Candy wrappers everywhere. Someone had a party, once.', 'A car alarm, somewhere. Then nothing.', 'Oil stains shaped like continents.'],
    specials: [], controllable: false,
  },
  downtown: {
    // Krug 5 (K4.11): a zone opens 3 levels before its recommended minimum. 3. 10. 2026 (package 2): 60 minutes
    // instead of 80 (5.e) and a run of 240 (5.a, the night run) from level 6, the zone's recommended minimum (owner
    // decision 3. 10.: on levels 3–5 the run of 240 came home for HP in 79–97% of runs); before: [20, 40, 80].
    id: 'downtown', name: 'Downtown', kind: 'Downtown', danger: 3, recommended: [6, 12], level: 9, durations: [20, 40, 60, 240], durationLevels: { 240: 6 }, energy: 2, pvp: 'similar', phase: 1, unlockLevel: 3,
    lootText: 'Food, Wood, Electronics, weapons',
    // Krug 5 (F5): less Electronics (a surplus), more Wood and Cloth for the buildings.
    resources: [['electronics', 12], ['food', 25], ['metal', 15], ['scrap', 15], ['cloth', 15], ['wood', 25]],
    extras: [['ammo', 6], ['bandage', 3], ['painkillers', 2], ['repair_kit', 1]],
    enemies: [['infected', 60], ['raider', 40]],
    itemChance: 0.08,
    pos: { x: 50, y: 46 }, icon: PIN.downtown,
    about: 'Halcyon’s tower still stands over the square. A MEND kiosk still offers doses to anyone walking by.',
    flavor: ['Rooftops, laundry lines, a bike on the third floor.', 'Every shop window is a mouth with no teeth.', 'Heard music. It was a wind chime.', 'Traffic lights still blink yellow on one corner.'],
    specials: [['lab_clue', 0.003]], controllable: true,
  },
  hospital: {
    // 3. 10. 2026 (package 2, 5.e): 60 minutes instead of 80; before: [20, 40, 80].
    id: 'hospital', name: 'Mercy General', kind: 'Hospital', danger: 4, recommended: [10, 16], level: 13, durations: [20, 40, 60], energy: 2, pvp: 'similar', phase: 1, unlockLevel: 7,
    lootText: 'Cloth, Wood, Medicine, medkits and drugs',
    // Krug 5 (F5): Ammo (Raiders), Food, Wood and Cloth for the zones of levels 10–16; less
    // Medicine, Chemicals and Electronics (a surplus).
    resources: [['medicine', 12], ['chemicals', 6], ['cloth', 25], ['electronics', 3], ['ammo', 10], ['food', 10], ['wood', 20]],
    extras: [['medkit', 5], ['painkillers', 5], ['stim', 3], ['bandage', 6], ['mend_shot', 1]],
    enemies: [['infected', 70], ['raider', 30]],
    itemChance: 0.08,
    // 7. 10. 2026: a little left (was 24), so its locked chip (1366 px, a new survivor) clears the Laboratory's tape.
    pos: { x: 21, y: 26 }, icon: PIN.hospital,
    about: 'The intake tent is still pitched by the doors. Mutants live in the basement and take no visitors.',
    flavor: ['The east wing hums. A generator, or something worse.', 'Gurneys stacked like a barricade someone lost.', 'Clipboards still on the doors. Names, dates, nothing else.', 'Footprints in the dust, fresh ones, not yours.'],
    // 1. 10. 2026 (C1): Old Maps too, for the Bunker's runs (a key a run).
    specials: [['lab_clue', 0.008], ['old_map', 0.006]], controllable: true,
  },
  factory: {
    id: 'factory', name: 'Calder Steelworks', kind: 'Factory', short: 'Steelworks', danger: 4, recommended: [10, 16], level: 13, durations: [60, 120, 240], energy: 2, pvp: 'similar', phase: 1, unlockLevel: 7,
    lootText: 'Metal, Wood, Scrap, crafting parts',
    resources: [['metal', 40], ['chemicals', 9], ['electronics', 6], ['scrap', 20], ['ammo', 12], ['wood', 25]],
    extras: [['repair_kit', 3], ['fuel', 5]],
    enemies: [['infected', 60], ['drone', 25], ['raider', 15]],
    itemChance: 0.07,
    pos: { x: 80, y: 34 }, icon: PIN.factory,
    about: 'The Foundry lit the blast furnaces again. Nobody knows what they build in the closed hall.',
    flavor: ['Conveyor belts, frozen mid-shift.', 'Something mechanical is still counting in the dark.', 'The loading dock smells like rain on rust.', 'Posters about safety. Nobody is safe.'],
    specials: [['lab_clue', 0.006], ['old_map', 0.006]], controllable: true,
  },
  military_base: {
    id: 'military_base', name: 'Fort Halden', kind: 'Military Base', danger: 5, recommended: [15, 20], level: 17, durations: [60, 120, 240], energy: 3, pvp: 'open', phase: 1, unlockLevel: 12,
    lootText: 'Ammo, Metal, Electronics, Military and Nightstalker gear',
    resources: [['ammo', 35], ['metal', 25], ['electronics', 6], ['fuel', 10], ['scrap', 10], ['food', 10], ['wood', 10]],
    extras: [['medkit', 3], ['stim', 2], ['repair_kit', 3]],
    enemies: [['infected', 40], ['raider', 40], ['drone', 20]],
    itemChance: 0.09,
    pos: { x: 54, y: 10 }, icon: PIN.military,
    about: 'The control center of the Final Protocol. WARDEN still runs drills for a city that is gone.',
    flavor: ['The gate was left open after the storm.', 'A watchtower. The searchlight is pointed at the ground.', 'Sandbags, splits, spent casings like seeds.', 'The motor pool is full of cars with no keys and no hope.'],
    // 1. 10. 2026 (C1): Old Maps and Lab Clues too, for players past the Hospital.
    specials: [['map_fragment', 0.005], ['old_map', 0.004], ['lab_clue', 0.005]], controllable: true,
  },
  mountains: {
    id: 'mountains', name: 'Pine Ridge', kind: 'Mountains', danger: 4, recommended: [14, 20], level: 16, durations: [120, 240], energy: 2, pvp: 'similar', phase: 2, unlockLevel: 14,
    lootText: 'Wood, Metal, Fuel, Old Maps, Map Fragments, Hunter gear',
    // Krug 5 (F5): old hunting camps; ranged builds found no Ammo here (0% of what they spent).
    resources: [['wood', 30], ['metal', 20], ['chemicals', 6], ['medicine', 5], ['fuel', 15], ['food', 15], ['ammo', 8]],
    extras: [['stim', 3], ['medkit', 2]],
    enemies: [['mutant', 55], ['infected', 45]],
    itemChance: 0.08,
    pos: { x: 14, y: 8 }, icon: PIN.mountains,
    about: 'Halcyon’s old sanatorium sits above the pines. The oldest Mutants live here, and one wing stays locked.',
    flavor: ['Thin air and a view of everything that ended.', 'A cable car hangs over the valley, swinging.', 'Snow over something that used to be a camp.'],
    specials: [['old_map', 0.008], ['map_fragment', 0.003]], controllable: true,
  },
  wasteland: {
    id: 'wasteland', name: 'The Burn', kind: 'Wasteland', danger: 5, recommended: [20, 20], level: 20, durations: [240, 480], energy: 3, pvp: 'open', phase: 3, unlockLevel: 20,
    // 2. 10. 2026 (package 1, 7.b): what the pool really gives (T1 24%, T2 24%, T3 12%, T4 39% of the item
    // weight; seven sets, no Rider); it said "top-tier gear of every set". Package 6 (7.b2) makes it T4.
    lootText: 'Chemicals, Metal, Fuel, Map Fragments, gear of every set but Rider (mostly T1–T3)',
    resources: [['chemicals', 15], ['metal', 25], ['electronics', 9], ['fuel', 20], ['ammo', 10], ['scrap', 10]],
    extras: [['medkit', 3], ['stim', 3]],
    enemies: [['mutant', 50], ['drone', 30], ['raider', 20]],
    itemChance: 0.1,
    pos: { x: 86, y: 8 }, icon: PIN.wasteland,
    about: 'WARDEN tried to clean this district with fire. When the wind turns, the ash still moves.',
    // Package 4, part H: The Burn is a district WARDEN burned, not a glowing desert (the world book).
    flavor: ['Ash drifts like snow that never melts.', 'A street of chimneys. The houses went somewhere.', 'A billboard, bleached white. Only the phone number survived.'],
    specials: [['map_fragment', 0.006]], controllable: true,
  },
  secret_bunker: {
    id: 'secret_bunker', name: 'Civil Defense Bunker', kind: 'Secret Bunker', short: 'Bunker', danger: 4, recommended: [12, 20], level: 15, durations: [60], energy: 2, pvp: 'similar', phase: 2, unlockLevel: 10, secret: true,
    lootText: 'Blueprints, Ammo, Electronics, Military and Nightstalker gear',
    resources: [['ammo', 35], ['electronics', 30], ['metal', 20], ['fuel', 15]],
    extras: [['blueprint', 3], ['medkit', 2], ['repair_kit', 2]],
    enemies: [['drone', 50], ['infected', 50]],
    itemChance: 0.1,
    pos: { x: 7, y: 42 }, icon: PIN.bunker,
    about: 'The city’s archive of blueprints, kept for a quarantine nobody planned to survive. Someone wrote Plan B on the door.',
    flavor: ['Blast door, half open. The other half never will be.', 'Bunks made with military corners. Nobody to inspect them.'],
    // 1. 10. 2026 (C1): every run here costs a key, so a run finds more Blueprints (30. 9.: 0.03).
    specials: [['blueprint', 0.25]], controllable: false,
  },
  underground_lab: {
    id: 'underground_lab', name: 'Halcyon Lab', kind: 'Underground Laboratory', danger: 5, recommended: [15, 20], level: 17, durations: [60], energy: 3, pvp: 'similar', phase: 4, unlockLevel: 12, secret: true,
    lootText: 'Chemicals, Electronics, Medicine, Blueprints',
    resources: [['chemicals', 40], ['electronics', 30], ['medicine', 30]],
    extras: [['stim', 4], ['medkit', 3], ['blueprint', 1], ['mend_shot', 1]],
    enemies: [['mutant', 60], ['drone', 40]],
    itemChance: 0.1,
    pos: { x: 37, y: 35 }, icon: PIN.lab,
    about: 'Under Halcyon’s tower, where MEND was made. Some of the machines are still running.',
    flavor: ['Emergency lights, still red after all this time.', 'Cages. Open. From the inside.'],
    specials: [['blueprint', 0.11]], controllable: false,
  },
  survivor_vault: {
    id: 'survivor_vault', name: 'The Vault', kind: 'Survivor Vault', danger: 5, recommended: [18, 20], level: 20, durations: [120], energy: 3, pvp: 'open', phase: 4, unlockLevel: 16, secret: true,
    lootText: 'Scrap, Electronics, Ammo, the best odds of Epic and Legendary gear',
    resources: [['scrap', 30], ['electronics', 25], ['ammo', 25], ['medicine', 20]],
    extras: [['medkit', 4], ['stim', 3]],
    enemies: [['raider', 50], ['drone', 50]],
    // 1. 10. 2026 (owner decision, package A–C): every run costs a key, so a run finds more gear (was 0.12).
    itemChance: 0.2,
    // 4. 10. 2026: 2% to the left, so its name clears the Factory's New tape on the big map (was x 70).
    pos: { x: 68, y: 19 }, icon: PIN.vault,
    about: 'A door that never opens and an intercom that always answers. The Sealed pay well for deliveries.',
    flavor: ['A door with a dial the size of a wagon wheel.', 'Canned food stacked to the ceiling, labels facing out.'],
    specials: [], controllable: false,
  },
};

/** The special finds of 30. 9. 2026 (before C1), for the simulations' VARIANT=a and b. */
const SPECIALS_30_9: Partial<Record<ZoneId, [StackKind, number][]>> = {
  hospital: [['lab_clue', 0.008]],
  factory: [['lab_clue', 0.006]],
  military_base: [['map_fragment', 0.004]],
  secret_bunker: [['blueprint', 0.03]],
  underground_lab: [['blueprint', 0.02]],
};

/** VARIANT=a and b (sims only): the secret zones of 30. 9.: free once found, keys only from the old places. */
export function useSecretsBefore1Oct() {
  SECRET_ENTRY.on = false;
  SPECIAL_FIND_XP.blueprint = 30;
  ZONES.survivor_vault.itemChance = 0.12;
  for (const [z, sp] of Object.entries(SPECIALS_30_9) as [ZoneId, [StackKind, number][]][]) ZONES[z].specials = sp.map((x) => [...x] as [StackKind, number]);
}

export const ZONE_ORDER: ZoneId[] = ['forest', 'gas_station', 'downtown', 'hospital', 'factory', 'military_base', 'mountains', 'wasteland'];
export const SECRET_ZONES: ZoneId[] = ['secret_bunker', 'underground_lab', 'survivor_vault'];
export const CAMP_POS = { x: 48, y: 82 };

/** Roads between zones (from the design doc flowchart). */
export const ROADS: [ZoneId | 'camp', ZoneId | 'camp'][] = [
  ['camp', 'forest'],
  ['camp', 'gas_station'],
  ['camp', 'downtown'],
  ['forest', 'downtown'],
  ['forest', 'mountains'],
  ['downtown', 'factory'],
  ['downtown', 'hospital'],
  ['gas_station', 'downtown'],
  ['gas_station', 'factory'],
  ['hospital', 'military_base'],
  ['downtown', 'military_base'],
  ['military_base', 'wasteland'],
  ['factory', 'wasteland'],
  ['mountains', 'hospital'],
];

export function zone(id: ZoneId): ZoneDef {
  const z = ZONES[id];
  if (!z) throw new Error(`Unknown zone: ${id}`);
  return z;
}

/**
 * Ticks of a run by the duration the player picked (krug 3, L1 / O1):
 * floor(5 × (minutes / 10)^0.8 + 0.5), at least 3. 10 → 5, 20 → 9, 40 → 15, 60 → 21,
 * 120 → 37, 480 → 111: a longer run always brings more in total, a bit less per minute.
 */
/** 3. 10. 2026 (package 2): the durations a survivor of `level` can pick in a zone (all of them without a level). */
export function zoneDurations(zone: ZoneId, level?: number): number[] {
  const z = ZONES[zone];
  return level == null ? z.durations : z.durations.filter((d) => level >= (z.durationLevels?.[d] ?? 0));
}

/** The level a zone's duration opens at, when it opens later than the zone (Downtown's 240: 6); else null. */
export function durationOpensAt(zone: ZoneId, minutes: number): number | null {
  return ZONES[zone].durationLevels?.[minutes] ?? null;
}

export function ticksFor(duration: number): number {
  return Math.max(3, Math.floor(5 * Math.pow(Math.max(0, duration) / 10, 0.8) + 0.5));
}
